import { CustomBuydownPlan, CustomBuydownTermRate, CustomBuydownTimeline } from '@src/types/CustomBuydown'
import { keepPreviousData, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

const SCOPE = 'custom-buydown'
const TIMELINE = 'timeline'
const TERM_RATES = 'term-rates'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  timeline: (merchantId: string) => [{ scope: SCOPE, entity: TIMELINE, merchantId }] as const,
  termRates: (merchantId: string, merchantFeeRate: number, borrowerFeeRate: number, maxFinancingAmount?: number) =>
    [{ scope: SCOPE, entity: TERM_RATES, merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount }] as const,
}

const getCustomBuydownTimeline = async ({
  queryKey: [{ merchantId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['timeline']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CustomBuydownTimeline>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown`,
  )
  return response.data
}

export function useCustomBuydownTimelineQuery(merchantId: string) {
  return useQuery({
    queryKey: keysFactory.timeline(merchantId),
    queryFn: getCustomBuydownTimeline,
    enabled: !!merchantId,
  })
}

export interface CreateCustomBuydownPlanDto {
  contribution: number
  activeFrom: string
  expiresOn?: string
  maxFinancingAmount?: number
}

const createCustomBuydownPlan = async ({
  merchantId,
  dto,
}: {
  merchantId: string
  dto: CreateCustomBuydownPlanDto
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CustomBuydownPlan>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown`,
    dto,
  )
  return response.data
}

export function useCreateCustomBuydownPlan(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateCustomBuydownPlanDto) => createCustomBuydownPlan({ merchantId, dto }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keysFactory.timeline(merchantId) })
    },
  })
}

const getCustomBuydownTermRates = async ({
  queryKey: [{ merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['termRates']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CustomBuydownTermRate[]>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/TermRates`,
    { params: { merchantFeeRate, borrowerFeeRate, maxFinancingAmount } },
  )
  return response.data
}

export function useCustomBuydownTermRates(
  merchantId: string,
  merchantFeeRate: number,
  borrowerFeeRate: number,
  maxFinancingAmount?: number,
) {
  return useQuery({
    queryKey: keysFactory.termRates(merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount),
    queryFn: getCustomBuydownTermRates,
    enabled: !!merchantId && merchantFeeRate > 0,
    placeholderData: keepPreviousData,
  })
}

const deleteCustomBuydownPlan = async ({ merchantId, promoPlanId }: { merchantId: string; promoPlanId: string }) => {
  const apiClient = getApiClient()
  await apiClient.delete(`/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/${encodeURIComponent(promoPlanId)}`)
}

export function useDeleteCustomBuydownPlan(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (promoPlanId: string) => deleteCustomBuydownPlan({ merchantId, promoPlanId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keysFactory.timeline(merchantId) }),
  })
}

const terminateCustomBuydownPlan = async ({ merchantId, promoPlanId }: { merchantId: string; promoPlanId: string }) => {
  const apiClient = getApiClient()
  await apiClient.post(`/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/${encodeURIComponent(promoPlanId)}/Terminate`)
}

export function useTerminateCustomBuydownPlan(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (promoPlanId: string) => terminateCustomBuydownPlan({ merchantId, promoPlanId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keysFactory.timeline(merchantId) }),
  })
}
